import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Upload, ArrowRight, AlertCircle, TrendingUp, TrendingDown, BarChart2, X, CheckCircle, MessageSquare, ChevronDown, ChevronUp, PieChart, Calendar, Loader2 } from 'lucide-react';

// 定数
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 36; // 最大36ファイル(3年分)

// インサイト検出の閾値
const INSIGHT_NEW_CONTRACT_THRESHOLD = 30000; // 新規契約と判定する金額（円）
const INSIGHT_INCREASE_THRESHOLD = 50000; // 利用増と判定する金額差（円）
const INSIGHT_HIGH_AMOUNT_THRESHOLD = 30000; // 高額決済と判定する金額（円）
const INSIGHT_CAMPAIGN_THRESHOLD = 50000; // キャンペーン検出の金額差（円）
const INSIGHT_CAMPAIGN_COUNT_THRESHOLD = 10; // キャンペーン検出の最小取引回数
const INSIGHT_TOP_ITEMS_COUNT = 8; // 増加/減少ランキングの表示件数

// グラフカラーパレット（増加系と減少系）- 36色まで対応
const INCREASE_COLORS = [
  'bg-gray-400',     // 1つ目は参照値として灰色
  // 赤系（増加を表現）
  'bg-red-400',
  'bg-orange-400',
  'bg-amber-400',
  'bg-pink-400',
  'bg-rose-400',
  'bg-red-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-pink-500',
  'bg-rose-500',
  'bg-red-600',
  // 追加の暖色系
  'bg-orange-600',
  'bg-amber-600',
  'bg-pink-600',
  'bg-rose-600',
  'bg-red-300',
  'bg-orange-300',
  'bg-amber-300',
  'bg-pink-300',
  'bg-rose-300',
  'bg-red-700',
  'bg-orange-700',
  'bg-amber-700',
  'bg-pink-700',
  'bg-rose-700',
  'bg-red-800',
  'bg-orange-800',
  'bg-amber-800',
  'bg-pink-800',
  'bg-rose-800',
  'bg-red-900',
  'bg-orange-900',
  'bg-amber-900',
  'bg-pink-900',
  'bg-rose-900',
];

const DECREASE_COLORS = [
  'bg-gray-400',     // 1つ目は参照値として灰色
  // 緑/青系（減少を表現）
  'bg-emerald-400',
  'bg-green-400',
  'bg-teal-400',
  'bg-cyan-400',
  'bg-sky-400',
  'bg-emerald-500',
  'bg-green-500',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-sky-500',
  'bg-emerald-600',
  // 追加の寒色系
  'bg-green-600',
  'bg-teal-600',
  'bg-cyan-600',
  'bg-sky-600',
  'bg-emerald-300',
  'bg-green-300',
  'bg-teal-300',
  'bg-cyan-300',
  'bg-sky-300',
  'bg-emerald-700',
  'bg-green-700',
  'bg-teal-700',
  'bg-cyan-700',
  'bg-sky-700',
  'bg-emerald-800',
  'bg-green-800',
  'bg-teal-800',
  'bg-cyan-800',
  'bg-sky-800',
  'bg-emerald-900',
  'bg-green-900',
  'bg-teal-900',
  'bg-cyan-900',
  'bg-sky-900',
];

// 安全なオブジェクトキーチェック
const isSafeKey = (key: string): boolean => {
  const dangerousKeys = ['__proto__', 'constructor', 'prototype'];
  return !dangerousKeys.includes(key);
};

// ファイル名のサニタイズ（強化版）
const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/[<>'"]/g, '')           // XSS対策
    .replace(/[./\\]/g, '')           // パストラバーサル対策
    .replace(/[\x00-\x1F\x7F]/g, ''); // 制御文字除去
};

// --- Types ---
type Transaction = {
  date: string;
  merchant: string;
  amount: number;
  monthStr: string; // YYYY-MM
  memo: string;
  user: string;
};

type MonthlyData = {
  monthStr: string;
  total: number;
  count: number;
  transactions: Transaction[];
  byMerchant: { [key: string]: number };
  byCategory: { [key: string]: number };
};

type Insight = {
  type: 'increase' | 'new' | 'spike' | 'campaign';
  title: string;
  description: string;
  amount: number;
  merchant: string;
};

type ChangeItem = {
  merchant: string;
  diff: number;
  prevAmount: number;
  currAmount: number;
};

type MonthAnalysis = {
  monthStr: string;
  prevMonthStr: string | null;
  diff: number;
  insights: Insight[];
  topIncreases: ChangeItem[];
  topDecreases: ChangeItem[];
};

// --- Helpers ---

// ファイル読み込みヘルパー
const readFileAsText = (file: File, encoding: string, fileName: string): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('ファイルの読み込みに失敗しました'));
      }
    };
    reader.onerror = () => reject(new Error(`ファイル「${fileName}」の読み込みエラー`));
    reader.readAsText(file, encoding);
  });
};

const guessCategory = (t: Transaction) => {
  const text = `${t.merchant} ${t.memo}`.toLowerCase();

  if (text.includes('amazon') && (text.includes('書籍') || text.includes('キャッシュバック') || text.includes('cp') || text.includes('ギフト'))) return '販促・キャンペーン';
  if (text.includes('amazon') || text.includes('monotaro') || text.includes('askul')) return '物品・備品';
  if (text.includes('line') || text.includes('google ads') || text.includes('facebook') || text.includes('meta') || text.includes('tiktok') || text.includes('twitter') || text.includes('yahoo')) return '広告・マーケティング';
  if (text.includes('zoom') || text.includes('chatwork') || text.includes('slack') || text.includes('notion') || text.includes('google') || text.includes('adobe') || text.includes('cloudsign') || text.includes('salesforce') || text.includes('hubspot') || text.includes('freee') || text.includes('moneyforward') || text.includes('cursor') || text.includes('vrew') || text.includes('usage')) return 'ツール・SaaS';
  if (text.includes('studio') || text.includes('coconala') || text.includes('lancers') || text.includes('crowdworks') || text.includes('domain') || text.includes('onamae') || text.includes('xserver') || text.includes('aws') || text.includes('sakura') || text.includes('conoha') || text.includes('server')) return '制作・Webインフラ';
  if (text.includes('japan post') || text.includes('yamato') || text.includes('sagawa') || text.includes('tax') || text.includes('uber') || text.includes('taxi') || text.includes('suica') || text.includes('pasmo')) return '事務・交通・配送';
  if (text.includes('cafe') || text.includes('coffee') || text.includes('restaurant') || text.includes('eats')) return '会議費・飲食';

  return 'その他';
};

const parseCSVLine = (text: string) => {
  const result = [];
  let start = 0;
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '"') {
      inQuotes = !inQuotes;
    } else if (text[i] === ',' && !inQuotes) {
      result.push(text.substring(start, i).replace(/^"|"$/g, '').replace(/""/g, '"'));
      start = i + 1;
    }
  }
  result.push(text.substring(start).replace(/^"|"$/g, '').replace(/""/g, '"'));
  return result;
};

// --- Components ---

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const InsightBadge = ({ type }: { type: Insight['type'] }) => {
  const styles = {
    increase: "bg-red-100 text-red-700 border-red-200",
    new: "bg-blue-100 text-blue-700 border-blue-200",
    spike: "bg-orange-100 text-orange-700 border-orange-200",
    campaign: "bg-purple-100 text-purple-700 border-purple-200",
  };
  const labels = {
    increase: "支出増",
    new: "新規発生",
    spike: "高額決済",
    campaign: "回数急増",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold border ${styles[type]}`}>
      {labels[type]}
    </span>
  );
};

const CategoryPieChart = ({ data }: { data: MonthlyData }) => {
  const sortedCategories = Object.entries(data.byCategory).sort((a, b) => b[1] - a[1]);
  const total = data.total || 1;

  let chartData: [string, number][] = [];
  let otherSum = 0;

  sortedCategories.forEach(([cat, val], idx) => {
    if (idx < 5) {
      chartData.push([cat, val]);
    } else {
      otherSum += val;
    }
  });

  const existingOtherIndex = chartData.findIndex(([cat]) => cat === 'その他');
  if (existingOtherIndex !== -1) {
    chartData[existingOtherIndex] = ['その他', chartData[existingOtherIndex][1] + otherSum];
  } else if (otherSum > 0) {
    chartData.push(['その他', otherSum]);
  }

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#6B7280'];
  let cumulativePercent = 0;

  const getCoordinatesForPercent = (percent: number) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-8 py-4">
      <div className="relative w-40 h-40 shrink-0">
        <svg viewBox="-1 -1 2 2" className="transform -rotate-90 w-full h-full">
          {chartData.map(([, amount], index) => {
            const percent = amount / total;
            if (percent <= 0) return null;
            const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
            cumulativePercent += percent;
            const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
            const largeArcFlag = percent > 0.5 ? 1 : 0;
            const pathData = percent >= 0.999
              ? `M 1 0 A 1 1 0 1 1 -1 0 A 1 1 0 1 1 1 0`
              : `M 0 0 L ${startX} ${startY} A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY} L 0 0`;

            return (
              <path
                key={`pie-${index}`}
                d={pathData}
                fill={colors[index % colors.length]}
                stroke="white"
                strokeWidth="0.02"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none drop-shadow-md">
          <span className="text-xs text-white font-bold bg-gray-800/60 px-2 py-0.5 rounded-full">Total</span>
        </div>
      </div>

      <div className="w-full max-w-xs space-y-3">
        {chartData.map(([cat, amount], index) => (
          <div key={`category-${index}`} className="flex justify-between items-center text-sm border-b border-gray-50 pb-1 last:border-0">
            <div className="flex items-center truncate mr-2">
              <span className="w-3 h-3 rounded-full mr-2 shrink-0 shadow-sm" style={{ backgroundColor: colors[index % colors.length] }}></span>
              <span className="truncate text-gray-700 font-medium" title={cat}>{cat}</span>
            </div>
            <div className="flex items-center shrink-0">
              <span className="text-gray-900 font-bold mr-2">¥{amount.toLocaleString()}</span>
              <span className="text-gray-400 text-xs w-10 text-right">({((amount / total) * 100).toFixed(1)}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main App ---

export default function UpsiderReportGenerator() {
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [analyses, setAnalyses] = useState<MonthAnalysis[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  // メモリリーク対策: コンポーネントアンマウント時にデータをクリア
  useEffect(() => {
    return () => {
      setMonthlyData([]);
      setAnalyses([]);
      setError(null);
    };
  }, []);

  const toggleMonth = useCallback((monthStr: string) => {
    setExpandedMonths(prevSet => {
      const newSet = new Set(prevSet);
      if (newSet.has(monthStr)) {
        newSet.delete(monthStr);
      } else {
        newSet.add(monthStr);
      }
      return newSet;
    });
  }, []);

  const processFiles = async (files: File[]) => {
    setIsProcessing(true);
    setError(null);
    const allTransactions: Transaction[] = [];

    try {
      // ファイル数チェック
      if (files.length > MAX_FILES) {
        throw new Error(`ファイル数が多すぎます。最大${MAX_FILES}ファイルまでアップロード可能です。`);
      }

      // ファイルサイズ・形式チェック
      for (const file of files) {
        const safeName = sanitizeFileName(file.name);

        if (file.size > MAX_FILE_SIZE) {
          throw new Error(`ファイル「${safeName}」のサイズが大きすぎます。最大${MAX_FILE_SIZE / 1024 / 1024}MBまでです。`);
        }

        if (!file.name.toLowerCase().endsWith('.csv')) {
          throw new Error(`ファイル「${safeName}」はCSVファイルではありません。`);
        }

        // MIMEタイプ検証（拡張子偽装対策）
        const validMimeTypes = ['text/csv', 'text/plain', 'application/csv'];
        if (file.type && !validMimeTypes.includes(file.type)) {
          throw new Error(`ファイル「${safeName}」の形式が無効です（MIMEタイプ: ${file.type}）`);
        }
      }

      for (const file of files) {
        const safeName = sanitizeFileName(file.name);
        let text: string;

        // Shift_JISで読み込み
        try {
          text = await readFileAsText(file, 'Shift_JIS', safeName);
        } catch (err) {
          throw new Error(`ファイル「${safeName}」の読み込みに失敗しました: ${err instanceof Error ? err.message : '不明なエラー'}`);
        }

        // UTF-8で再試行
        if (!text.includes('取引日')) {
          try {
            text = await readFileAsText(file, 'UTF-8', safeName);
          } catch (err) {
            throw new Error(`ファイル「${safeName}」はUpsider形式のCSVではありません`);
          }
        }

        const lines = text.split(/\r\n|\n/);
        const headerIndex = lines.findIndex(line => line.includes('取引日') && line.includes('出金金額'));
        if (headerIndex === -1) continue;

        const headers = parseCSVLine(lines[headerIndex]);
        const dateIdx = headers.indexOf('取引日');
        const merchantIdx = headers.indexOf('利用先');
        const amountIdx = headers.indexOf('出金金額');
        const memoIdx = headers.indexOf('メモ');
        const userIdx = headers.indexOf('カード保有者名');

        if (dateIdx === -1 || amountIdx === -1) continue;

        for (let i = headerIndex + 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          const row = parseCSVLine(lines[i]);
          if (row.length <= Math.max(dateIdx, amountIdx)) continue;

          const dateStr = row[dateIdx];
          const amountStr = row[amountIdx];
          const merchant = row[merchantIdx] || '不明';
          const memo = memoIdx !== -1 ? row[memoIdx] : '';
          const user = userIdx !== -1 ? row[userIdx] : '';

          if (!dateStr || !amountStr) continue;
          const amount = parseInt(amountStr.replace(/,/g, ''), 10);

          if (!isNaN(amount) && amount > 0) {
            const dateParts = dateStr.split('/');
            if (dateParts.length === 3) {
              const monthStr = `${dateParts[0]}-${dateParts[1]}`;
              allTransactions.push({
                date: dateStr,
                merchant: merchant.trim(),
                amount,
                monthStr,
                memo,
                user
              });
            }
          }
        }
      }

      if (allTransactions.length === 0) throw new Error("有効なデータが見つかりませんでした");

      const grouped: { [key: string]: MonthlyData } = {};
      allTransactions.forEach(t => {
        // プロトタイプ汚染対策
        if (!isSafeKey(t.monthStr) || !isSafeKey(t.merchant)) {
          return; // 危険なキーはスキップ
        }

        if (!grouped[t.monthStr]) {
          grouped[t.monthStr] = {
            monthStr: t.monthStr,
            total: 0,
            count: 0,
            transactions: [],
            byMerchant: Object.create(null),  // プロトタイプなしオブジェクト
            byCategory: Object.create(null)   // プロトタイプなしオブジェクト
          };
        }
        const m = grouped[t.monthStr];
        m.total += t.amount;
        m.count += 1;
        m.transactions.push(t);
        m.byMerchant[t.merchant] = (m.byMerchant[t.merchant] || 0) + t.amount;
        const cat = guessCategory(t);
        if (isSafeKey(cat)) {
          m.byCategory[cat] = (m.byCategory[cat] || 0) + t.amount;
        }
      });

      const sortedData = Object.values(grouped).sort((a, b) => a.monthStr.localeCompare(b.monthStr));
      setMonthlyData(sortedData);
      setExpandedMonths(new Set(sortedData.map(d => d.monthStr)));
      generateReport(sortedData);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '予期しないエラーが発生しました';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const generateReport = useCallback((data: MonthlyData[]) => {
    const report: MonthAnalysis[] = [];

    for (let i = 0; i < data.length; i++) {
      const curr = data[i];
      const prev = i > 0 ? data[i-1] : null;
      const insights: Insight[] = [];

      const diff = prev ? curr.total - prev.total : 0;

      const allMerchants = new Set([
        ...Object.keys(curr.byMerchant),
        ...(prev ? Object.keys(prev.byMerchant) : [])
      ]);

      const increases: ChangeItem[] = [];
      const decreases: ChangeItem[] = [];

      allMerchants.forEach(m => {
        const currVal = curr.byMerchant[m] || 0;
        const prevVal = prev ? (prev.byMerchant[m] || 0) : 0;
        const d = currVal - prevVal;

        if (d > 0) {
          increases.push({
            merchant: m,
            diff: d,
            prevAmount: prevVal,
            currAmount: currVal
          });
        } else if (d < 0) {
          decreases.push({
            merchant: m,
            diff: d,
            prevAmount: prevVal,
            currAmount: currVal
          });
        }
      });

      increases.sort((a, b) => b.diff - a.diff);
      decreases.sort((a, b) => a.diff - b.diff);

      Object.keys(curr.byMerchant).forEach(merchant => {
        const currAmount = curr.byMerchant[merchant];
        const prevAmount = prev ? (prev.byMerchant[merchant] || 0) : 0;
        const merchantDiff = currAmount - prevAmount;

        if (prevAmount === 0 && currAmount > INSIGHT_NEW_CONTRACT_THRESHOLD) {
          insights.push({
            type: 'new',
            title: `新規契約: ${merchant}`,
            description: `前月まで利用がありませんでしたが、今月¥${currAmount.toLocaleString()}の支払いが発生しています。`,
            amount: currAmount,
            merchant
          });
        } else if (merchantDiff > INSIGHT_INCREASE_THRESHOLD) {
          insights.push({
            type: 'increase',
            title: `利用増: ${merchant}`,
            description: `前月比 +¥${merchantDiff.toLocaleString()} 増加しています。`,
            amount: merchantDiff,
            merchant
          });
        }
      });

      const amazonTx = curr.transactions.filter(t => t.merchant.toLowerCase().includes('amazon'));
      if (amazonTx.length > INSIGHT_CAMPAIGN_COUNT_THRESHOLD) {
        const campaignMemo = amazonTx.find(t => t.memo.includes('書籍') || t.memo.includes('キャッシュバック'));
        const totalAmazon = amazonTx.reduce((sum, t) => sum + t.amount, 0);
        const prevAmazon = prev ? prev.transactions.filter(t => t.merchant.toLowerCase().includes('amazon')).reduce((sum, t) => sum + t.amount, 0) : 0;

        if (totalAmazon - prevAmazon > INSIGHT_CAMPAIGN_THRESHOLD) {
           insights.push({
            type: 'campaign',
            title: 'Amazonでの大量購入/キャンペーン',
            description: `決済回数が${amazonTx.length}回に達しています。メモ: 「${campaignMemo ? campaignMemo.memo : '使途不明'}」`,
            amount: totalAmazon,
            merchant: 'Amazon'
          });
        }
      }

      // 高額決済のチェック（最適化: Setを使用してO(n)に改善）
      const processedMerchants = new Set(insights.map(ins => ins.merchant));
      curr.transactions.forEach(t => {
        if (t.amount > INSIGHT_HIGH_AMOUNT_THRESHOLD && !t.merchant.includes('AMAZON')) {
          if (!processedMerchants.has(t.merchant)) {
            processedMerchants.add(t.merchant);
            insights.push({
              type: 'spike',
              title: `高額決済: ${t.merchant}`,
              description: `1回あたり¥${t.amount.toLocaleString()}の決済があります。メモ: ${t.memo || 'なし'}`,
              amount: t.amount,
              merchant: t.merchant
            });
          }
        }
      });

      const uniqueInsights = Array.from(new Map(insights.map(item => [item.title, item])).values());
      uniqueInsights.sort((a, b) => b.amount - a.amount);

      report.push({
        monthStr: curr.monthStr,
        prevMonthStr: prev ? prev.monthStr : null,
        diff,
        insights: uniqueInsights,
        topIncreases: increases.slice(0, INSIGHT_TOP_ITEMS_COUNT),
        topDecreases: decreases.slice(0, INSIGHT_TOP_ITEMS_COUNT)
      });
    }
    setAnalyses(report);
  }, []);

  const maxTotal = useMemo(() => {
    if (monthlyData.length === 0) return 1;
    return Math.max(...monthlyData.map(d => d.total)) || 1;
  }, [monthlyData]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans text-gray-800">
      <div className="max-w-5xl mx-auto">

        <header className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 gap-4">
          <div className="flex items-center gap-3">
             <div className="bg-blue-700 p-2.5 rounded-lg shadow-md">
              <TrendingUp className="text-white w-6 h-6" />
             </div>
             <div>
               <h1 className="text-2xl font-bold text-gray-900">月次コスト分析ダッシュボード</h1>
               <p className="text-xs text-gray-500">Upsider明細CSVを自動解析・レポート作成</p>
             </div>
          </div>

          {monthlyData.length > 0 && (
             <button
              onClick={() => { setMonthlyData([]); setAnalyses([]); setError(null); }}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center text-sm transition shadow-sm"
            >
              <X className="w-4 h-4 mr-2" /> リセット
            </button>
          )}
        </header>

        {monthlyData.length === 0 && !isProcessing && (
          <Card className="p-12 text-center border-2 border-dashed border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer relative group">
             <input
              type="file"
              multiple
              accept=".csv"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              onChange={(e) => e.target.files && processFiles(Array.from(e.target.files))}
              disabled={isProcessing}
            />
            <div className="bg-white p-4 rounded-full inline-block mb-4 shadow-sm group-hover:scale-110 transition-transform">
              <Upload className="w-8 h-8 text-blue-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">分析用CSVをドロップ</h2>
            <p className="text-gray-500">任意の期間のCSVファイルをまとめて入れてください<br/><span className="text-xs text-gray-400">（Shift_JIS / UTF-8 自動対応 / 最大{MAX_FILE_SIZE / 1024 / 1024}MB）</span></p>
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <div className="text-left">
                    <p className="text-sm font-bold text-red-700 mb-1">エラーが発生しました</p>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {isProcessing && (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">データを処理中...</h2>
              <p className="text-gray-500 text-sm">CSVファイルを解析しています。しばらくお待ちください。</p>
            </div>
          </Card>
        )}

        {analyses.length > 0 && (
          <div className="space-y-8 animate-in fade-in duration-500">

            {/* Summary Chart (Fixed Layout) */}
            <Card className="p-6 bg-white">
              <h3 className="text-sm font-bold text-gray-500 mb-4 flex items-center">
                <BarChart2 className="w-4 h-4 mr-2" /> 支出推移サマリー
              </h3>

              <div className="relative h-72 mt-8 pb-8 w-full">
                 {/* Flex container for bars, aligned to bottom */}
                 <div className="absolute inset-0 flex items-end justify-between gap-2 md:gap-4">
                    {monthlyData.map((m, idx) => {
                       const h = (m.total / maxTotal) * 100;
                       const analysis = analyses[idx];
                       const isIncrease = analysis ? analysis.diff > 0 : false;

                       // 各月ごとに異なる色を割り当て
                       const colorPalette = idx === 0 ? INCREASE_COLORS : (isIncrease ? INCREASE_COLORS : DECREASE_COLORS);
                       const barColor = colorPalette[idx % colorPalette.length];
                       const textColor = idx === 0 ? 'text-gray-600' : (isIncrease ? 'text-red-600' : 'text-emerald-600');

                       return (
                         <div key={m.monthStr} className="flex-1 h-full flex flex-col justify-end relative group">
                           {/* 1. Label Area (Absolute above bar) */}
                           <div
                             className="absolute w-full text-center transition-all duration-500"
                             style={{ bottom: `${Math.max(h, 1)}%`, marginBottom: '8px' }}
                           >
                             <div className={`text-sm md:text-lg font-bold ${textColor}`}>
                               ¥{m.total.toLocaleString()}
                             </div>
                             {/* Diff Badge (except first month) */}
                             {idx > 0 && (
                               <div className={`text-xs font-bold mt-1 ${isIncrease ? 'text-red-500' : 'text-green-500'}`}>
                                 {isIncrease ? '+' : ''}{analysis.diff.toLocaleString()}
                               </div>
                             )}
                           </div>

                           {/* 2. Bar Area */}
                           <div
                             className={`w-full rounded-t-md transition-all duration-700 ${barColor} relative shadow-sm hover:opacity-90`}
                             style={{ height: `${Math.max(h, 1)}%` }}
                           >
                             {/* Shine effect */}
                             <div className="absolute top-0 left-0 right-0 h-[1px] bg-white/40"></div>
                           </div>

                           {/* 3. Axis Area (Absolute below) */}
                           <div className="absolute -bottom-8 w-full text-center">
                              <span className="inline-block bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1 rounded-full">
                                {m.monthStr.split('-')[1]}月
                              </span>
                           </div>
                         </div>
                       )
                    })}
                 </div>
              </div>
            </Card>

            <div className="space-y-8">
              {analyses.slice(1).map((month) => {
                const maxIncrease = month.topIncreases.length > 0 ? month.topIncreases[0].diff : 1;
                const maxDecrease = month.topDecreases.length > 0 ? Math.abs(month.topDecreases[0].diff) : 1;
                const currentMonthData = monthlyData.find(d => d.monthStr === month.monthStr);

                return (
                  <div key={month.monthStr} className="relative">
                    <div className="flex items-start gap-4">
                      <div className="hidden md:flex flex-shrink-0 w-14 h-14 bg-white border-2 border-blue-100 rounded-full items-center justify-center shadow-sm z-10 text-lg font-bold text-blue-700 mt-4">
                        {month.monthStr.split('-')[1]}月
                      </div>

                      <Card className="flex-1 overflow-hidden border-l-4 border-l-blue-500">
                        <div
                          className="px-6 py-5 border-b border-gray-100 flex flex-col md:flex-row justify-between md:items-center bg-gray-50/30 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => toggleMonth(month.monthStr)}
                        >
                          <div>
                            <h2 className="text-xl font-bold text-gray-800 flex items-center mb-2 md:mb-0">
                              <span className="md:hidden mr-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-sm">{month.monthStr.split('-')[1]}月</span>
                              <Calendar className="inline w-4 h-4 mr-1 text-gray-400" />
                              {month.monthStr}
                              <span className="text-gray-400 text-sm font-normal ml-2">の分析レポート</span>
                            </h2>
                          </div>

                          <div className="flex items-center gap-4">
                             <div className="text-right">
                                <div className="text-xs text-gray-400 font-medium mb-0.5">前月比 (全体差分)</div>
                                <div className={`text-4xl font-black tracking-tighter ${month.diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {month.diff > 0 ? '+' : ''}¥{month.diff.toLocaleString()}
                                </div>
                             </div>
                             {expandedMonths.has(month.monthStr) ? <ChevronUp className="text-gray-400 w-6 h-6" /> : <ChevronDown className="text-gray-400 w-6 h-6" />}
                          </div>
                        </div>

                        {expandedMonths.has(month.monthStr) && (
                          <div className="divide-y divide-gray-100">

                            {currentMonthData && (
                              <div className="p-6 bg-white">
                                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                                  <PieChart className="w-4 h-4 mr-2" /> カテゴリ別支出内訳
                                </h3>
                                <CategoryPieChart data={currentMonthData} />
                              </div>
                            )}

                            {month.insights.length > 0 && (
                              <div className="p-6 bg-white">
                                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center">
                                  <MessageSquare className="w-4 h-4 mr-2" /> 分析レポート (AI Insight)
                                </h3>
                                <div className="space-y-4">
                                  {month.insights.map((insight, i) => (
                                    <div key={i} className="flex gap-4 items-start p-4 bg-gray-50 rounded-xl border border-gray-100">
                                      <div className="mt-1">
                                        {insight.type === 'campaign' ? <AlertCircle className="w-6 h-6 text-purple-500" /> :
                                         insight.type === 'new' ? <CheckCircle className="w-6 h-6 text-blue-500" /> :
                                         <TrendingUp className="w-6 h-6 text-red-500" />}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                          <span className="font-bold text-gray-900 text-base">{insight.title}</span>
                                          <InsightBadge type={insight.type} />
                                        </div>
                                        <p className="text-sm text-gray-600 leading-relaxed">
                                          {insight.description}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* 増加ランキング */}
                            <div className="p-6 bg-white">
                              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center">
                                <TrendingUp className="w-4 h-4 mr-2" /> 増加要因ランキング (プラス費用)
                              </h3>

                              <div className="space-y-3">
                                {month.topIncreases.length === 0 ? (
                                  <div className="text-center text-gray-400 py-4">増加要因はありません</div>
                                ) : (
                                  month.topIncreases.map((item, idx) => {
                                    const widthPercent = (item.diff / maxIncrease) * 100;
                                    return (
                                      <div key={idx} className="relative p-4 rounded-xl border border-gray-100 bg-white shadow-sm transition-all">
                                        <div
                                          className="absolute bottom-0 left-0 top-0 bg-red-50 rounded-xl -z-10 transition-all duration-500"
                                          style={{ width: `${Math.max(widthPercent, 2)}%`, opacity: 0.6 }}
                                        />
                                        <div className="flex justify-between items-center relative z-10">
                                          <div className="flex-1 min-w-0 pr-6">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="font-bold text-lg text-gray-800 truncate">{item.merchant}</span>
                                              <span className="font-black text-xl text-red-600 ml-4 shrink-0">+¥{item.diff.toLocaleString()}</span>
                                            </div>
                                            <div className="flex items-center text-sm text-gray-500">
                                              <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600 mr-2 text-xs font-bold">推移</span>
                                              <span>¥{item.prevAmount.toLocaleString()}</span>
                                              <ArrowRight className="w-4 h-4 mx-2 text-gray-400" />
                                              <span className="font-medium text-gray-700">¥{item.currAmount.toLocaleString()}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>

                            {/* 減少ランキング (新規追加) */}
                            <div className="p-6 bg-white border-t border-gray-100">
                              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center">
                                <TrendingDown className="w-4 h-4 mr-2" /> 減少要因ランキング (コスト削減)
                              </h3>

                              <div className="space-y-3">
                                {month.topDecreases.length === 0 ? (
                                  <div className="text-center text-gray-400 py-4">目立った減少要因はありません</div>
                                ) : (
                                  month.topDecreases.map((item, idx) => {
                                    const widthPercent = (Math.abs(item.diff) / maxDecrease) * 100;
                                    return (
                                      <div key={idx} className="relative p-4 rounded-xl border border-gray-100 bg-white shadow-sm transition-all">
                                        <div
                                          className="absolute bottom-0 left-0 top-0 bg-emerald-50 rounded-xl -z-10 transition-all duration-500"
                                          style={{ width: `${Math.max(widthPercent, 2)}%`, opacity: 0.6 }}
                                        />
                                        <div className="flex justify-between items-center relative z-10">
                                          <div className="flex-1 min-w-0 pr-6">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="font-bold text-lg text-gray-800 truncate">{item.merchant}</span>
                                              <span className="font-black text-xl text-emerald-600 ml-4 shrink-0">¥{item.diff.toLocaleString()}</span>
                                            </div>
                                            <div className="flex items-center text-sm text-gray-500">
                                              <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600 mr-2 text-xs font-bold">推移</span>
                                              <span>¥{item.prevAmount.toLocaleString()}</span>
                                              <ArrowRight className="w-4 h-4 mx-2 text-gray-400" />
                                              <span className="font-medium text-gray-700">¥{item.currAmount.toLocaleString()}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>

                          </div>
                        )}
                      </Card>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
